// import node module libraries
import React, { Fragment, useState, useEffect} from 'react';
import { Link } from 'react-router-dom';
import { Button, Col, Row, Tab, Card, Nav} from 'react-bootstrap';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

// import sub custom components
import FAQSettingsTable from './FAQSettingsTable';
import OffCanvasCreateFAQ from './OffcanvasCreateFAQ';

const AllFAQSettings = ({ activeKey }) => { // activeKey als Prop erhalten
	const { t } = useTranslation(); // useTranslation-Hook
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });

	const [page, setPage] = useState(1);
	const [FAQdata, setFAQData] = useState([]);
	const [isLoadingTable, setIsLoadingTable] = useState(true); // State for loading table
	const [createdNewFAQ, setCreatedNewFAQ] = useState(false); // State to trigger re-fetch

	// fetch data from api
	const authHeader = useAuthHeader();
	const options = {
		params: {

		},
		headers: {
			Authorization: authHeader,
			Accept: 'application/json'
		}
	};

	// Get data from API

	const getData = async () => {
		setIsLoadingTable(true);

		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.get(`${baseURL}/public/faqs`, options)
			.then(response => {
				setFAQData({ data: response.data.data });
				setIsLoadingTable(false);
			})
			.catch((error) => {
				const errorMessage = error.response?.data?.message
					? error.message + '. ' + error.response.data.message
					: error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
				setIsLoadingTable(false);
			});
	}

	useEffect(() => {
		// Check if the current tab is active before making the API call
		if (activeKey === 'faqs') {
			getData();
		}
	}, [activeKey, createdNewFAQ]); // Run effect when activeKey, filterBy, or page changes

	// Create FAQ
	const [isLoadingButtonCreateFAQ, setIsLoadingButtonCreateFAQ] = useState(null); // State for loading spinner
	const [showCreateFAQOffcanvas, setShowCreateFAQOffcanvas] = useState(false);
	const handleShowCreateFAQ = () => setShowCreateFAQOffcanvas(true);
	const handleCloseCreateFAQ = () => setShowCreateFAQOffcanvas(false);

	const handleCreateFAQ = async (e) => {
		e.preventDefault();
		setIsLoadingButtonCreateFAQ(true);

		const data = {
			faq_title: e.target.title.value,
			faq_content: e.target.content.value,
			faq_categories:
				e.target.categories.value.split(',').map((category) => [(category.trim())])
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.post(`${baseURL}/public/faqs`, data, options)
			.then(response => {
				// Trigger a re-fetch of the data
				setCreatedNewFAQ(true);
				// Close the offcanvas
				setShowCreateFAQOffcanvas(false);
				setIsLoadingButtonCreateFAQ(false);
				// Reset the form fields
				e.target.reset();
				// Show success notification
				handleShowNotification(setNotification, 'success', 'Erfolgreich', 'Deine Daten wurden erfolgreich gespeichert');
			})
			.catch((error) => {
				setShowCreateFAQOffcanvas(false);
				setIsLoadingButtonCreateFAQ(false);
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
			});
	};

	return (
		<Fragment>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			<Row>
				{/* Create FAQ */}
				<Col xl={{ offset: 1, span: 10 }} lg={{ offset: 1, span: 10 }} md={12}>
					<div className="border-bottom pb-4 mb-4 d-flex align-items-center justify-content-end">
						<Button variant="secondary" className="me-2" onClick={handleShowCreateFAQ}>
							{t('Create new FAQ')}
						</Button>
					</div>
				</Col>
			</Row>
			<Row>
				<Col xl={{ offset: 1, span: 10 }} lg={{ offset: 1, span: 10 }} md={12}>
					<Tab.Container defaultActiveKey="all">
						<Card>
							<Card.Header className="border-bottom-0 p-0 bg-white">
								<Nav className="nav-lb-tab">
									<Nav.Item>
										<Nav.Link eventKey="all" className="mb-sm-3 mb-md-0">
											{t('All FAQs')}
										</Nav.Link>
									</Nav.Item>
									<Nav.Item>
										<Nav.Link eventKey="mostasked" className="mb-sm-3 mb-md-0">
											{t('Most Asked')}
										</Nav.Link>
									</Nav.Item>
									<Nav.Item>
										<Nav.Link eventKey="general" className="mb-sm-3 mb-md-0">
											{t('General')}
										</Nav.Link>
									</Nav.Item>
									<Nav.Item>
										<Nav.Link eventKey="support" className="mb-sm-3 mb-md-0">
											{t('Support')}
										</Nav.Link>
									</Nav.Item>
								</Nav>
							</Card.Header>
							<Card.Body className="p-0">
								<Tab.Content>
									<Tab.Pane eventKey="all" className="pb-4">
										<FAQSettingsTable activeKey={activeKey} faqdata={FAQdata} isLoadingTable={isLoadingTable} filterBy="" />
									</Tab.Pane>
									<Tab.Pane eventKey="mostasked" className="pb-4">
										<FAQSettingsTable activeKey={activeKey} faqdata={FAQdata} isLoadingTable={isLoadingTable} filterBy="mostasked" />
									</Tab.Pane>
									<Tab.Pane eventKey="general" className="pb-4">
										<FAQSettingsTable activeKey={activeKey} faqdata={FAQdata} isLoadingTable={isLoadingTable} filterBy="general" />
									</Tab.Pane>
									<Tab.Pane eventKey="support" className="pb-4">
										<FAQSettingsTable activeKey={activeKey} faqdata={FAQdata} isLoadingTable={isLoadingTable} filterBy="support" />
									</Tab.Pane>
								</Tab.Content>
							</Card.Body>
						</Card>
					</Tab.Container>
				</Col>
			</Row>
			{/* Create FAQ Offcanvas */}
			<OffCanvasCreateFAQ
				show={showCreateFAQOffcanvas}
				onHide={handleCloseCreateFAQ}
				onSubmit={handleCreateFAQ}
				isLoadingButton={isLoadingButtonCreateFAQ}
			/>
		</Fragment>
	);
};

export default AllFAQSettings;
